// Graph helpers for the visual process map (routes/_app/process-map.tsx):
// node keys, the neighbours of a stage (previous / next in its lane and the
// hand-offs in and out), what to highlight for a focus, and the SVG path of
// an arrow between two node boxes. Pure functions, unit-tested.

import { processes, processIds, processLinks, type ProcessId, type ProcessLink } from "./processes";

export type NodeKey = `${ProcessId}.${string}`;

export const nodeKey = (process: ProcessId, stage: string): NodeKey => `${process}.${stage}`;

export function parseNodeKey(key: string): { process: ProcessId; stage: string } | null {
  const dot = key.indexOf(".");
  if (dot < 1) return null;
  const process = key.slice(0, dot) as ProcessId;
  const stage = key.slice(dot + 1);
  if (!processIds.includes(process) || !processes[process].some((s) => s.key === stage)) return null;
  return { process, stage };
}

export interface Neighbour {
  key: NodeKey;
  process: ProcessId;
  stage: string;
  /** Hand-off between lanes (null for the previous / next step in the same lane). */
  link: ProcessLink | null;
}

/** What comes before a stage (previous step, incoming hand-offs) and what follows it. */
export function neighboursOf(process: ProcessId, stage: string): { from: Neighbour[]; to: Neighbour[] } {
  const lane = processes[process];
  const i = lane.findIndex((s) => s.key === stage);
  const from: Neighbour[] = [];
  const to: Neighbour[] = [];
  const prev = i > 0 ? lane[i - 1] : undefined;
  const next = i >= 0 && i < lane.length - 1 ? lane[i + 1] : undefined;
  if (prev) from.push({ key: nodeKey(process, prev.key), process, stage: prev.key, link: null });
  if (next) to.push({ key: nodeKey(process, next.key), process, stage: next.key, link: null });
  for (const link of processLinks) {
    if (link.to.process === process && link.to.stage === stage) {
      from.push({ key: nodeKey(link.from.process, link.from.stage), ...link.from, link });
    }
    if (link.from.process === process && link.from.stage === stage) {
      to.push({ key: nodeKey(link.to.process, link.to.stage), ...link.to, link });
    }
  }
  return { from, to };
}

export type Focus = { kind: "stage"; key: NodeKey } | { kind: "process"; id: ProcessId } | { kind: "ledger" } | null;

/** Nodes and hand-offs to highlight for a focus; null = everything at full strength. */
export function highlightOf(focus: Focus): { nodes: Set<NodeKey>; links: Set<string> } | null {
  if (!focus) return null;
  const nodes = new Set<NodeKey>();
  const links = new Set<string>();
  if (focus.kind === "ledger") {
    for (const id of processIds) for (const s of processes[id]) if (s.posts) nodes.add(nodeKey(id, s.key));
    return { nodes, links };
  }
  if (focus.kind === "process") {
    for (const s of processes[focus.id]) nodes.add(nodeKey(focus.id, s.key));
    for (const link of processLinks) {
      if (link.from.process === focus.id || link.to.process === focus.id) {
        links.add(link.key);
        nodes.add(nodeKey(link.from.process, link.from.stage));
        nodes.add(nodeKey(link.to.process, link.to.stage));
      }
    }
    return { nodes, links };
  }
  const parsed = parseNodeKey(focus.key);
  if (!parsed) return null;
  nodes.add(focus.key);
  const { from, to } = neighboursOf(parsed.process, parsed.stage);
  for (const n of [...from, ...to]) {
    nodes.add(n.key);
    if (n.link) links.add(n.link.key);
  }
  return { nodes, links };
}

export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Arrow between two node boxes (canvas coordinates): from the bottom edge
 * down to the top edge of a lower lane, the reverse for a higher lane, and
 * an arc over the lane for a hand-off inside one lane. A small horizontal
 * spread keeps arrows that share a node apart.
 */
export function arrowPath(a: Box, b: Box, spread = 0): string {
  const ax = a.x + a.w / 2 + spread;
  const bx = b.x + b.w / 2 + spread;
  if (Math.abs(a.y - b.y) < 4) {
    const y = Math.min(a.y, b.y);
    const lift = Math.min(48, 18 + Math.abs(bx - ax) / 8);
    return `M ${r(ax)} ${r(y)} C ${r(ax)} ${r(y - lift)}, ${r(bx)} ${r(y - lift)}, ${r(bx)} ${r(y)}`;
  }
  const down = b.y > a.y;
  const sy = down ? a.y + a.h : a.y;
  const ty = down ? b.y : b.y + b.h;
  const bend = Math.max(24, Math.abs(ty - sy) / 2);
  const c1 = down ? sy + bend : sy - bend;
  const c2 = down ? ty - bend : ty + bend;
  return `M ${r(ax)} ${r(sy)} C ${r(ax)} ${r(c1)}, ${r(bx)} ${r(c2)}, ${r(bx)} ${r(ty)}`;
}

const r = (v: number) => Math.round(v * 10) / 10;

/**
 * Steps whose texts match a search: every word of the query must appear in
 * the step's text (title, description, process, screen names and codes),
 * case- and accent-insensitive. null = no search.
 */
export function matchStages(query: string, textOf: (process: ProcessId, stage: string) => string): Set<NodeKey> | null {
  const words = fold(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return null;
  const out = new Set<NodeKey>();
  for (const id of processIds) {
    for (const s of processes[id]) {
      const text = fold(textOf(id, s.key));
      if (words.every((w) => text.includes(w))) out.add(nodeKey(id, s.key));
    }
  }
  return out;
}

export const fold = (v: string) =>
  v
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ł/g, "l");
